import React, { useMemo } from "react";
import { useCoreContext } from "../../../../context/global/CoreContext";
import { Calendar } from "../../../../components/icons/svg/outline";
import { filterJobsByDateRange } from "../../../../utils/business/jobs";
import { getTodayInPT } from "../../../../utils/datetime";

/**
 * JobInformationSection Component
 * Displays/edits job information for T&M forms
 *
 * @param {Object} props Component props.
 * @param {Object} props.data Form data (`job`, `date_performed`, optional `job_number`).
 * @param {Function} props.onChange Called with the next data object.
 * @param {boolean} [props.readOnly=false] Disables job and date inputs.
 * @returns {JSX.Element}
 */
const JobInformationSection = ({ data, onChange, readOnly = false }) => {
  const { allJobsList } = useCoreContext();

  // Filter out jobs that ended more than 1 year ago
  const activeJobsList = useMemo(() => {
    return filterJobsByDateRange(allJobsList);
  }, [allJobsList]);

  /**
   * Persist the selected job id onto `data.job`.
   *
   * @param {React.ChangeEvent<HTMLSelectElement>} e Job select change event.
   * @returns {void}
   */
  const handleJobChange = (e) => {
    const jobId = parseInt(e.target.value);
    onChange?.({ ...data, job: jobId });
  };

  /**
   * Persist `date_performed`.
   *
   * @param {React.ChangeEvent<HTMLInputElement>} e Date input change event.
   * @returns {void}
   */
  const handleDateChange = (e) => {
    onChange?.({ ...data, date_performed: e.target.value });
  };

  const selectedJob = allJobsList?.find(job => job.id === data?.job);

  // For read-only mode, try to use job_number from API response first, then fallback to lookup
  const displayJobNumber = readOnly && data?.job_number
    ? data.job_number
    : selectedJob?.number;

  const displayJobDescription = readOnly && data?.job_description
    ? ` - ${data.job_description}`
    : selectedJob?.description ? ` - ${selectedJob.description}` : '';

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold text-gray-700 flex items-center gap-2">
        <Calendar className="w-5 h-5 text-emerald-800" />
        Job Information {readOnly && "(Read Only)"}
      </h2>

      <div className="grid grid-cols-2 gap-1">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Job ID *
          </label>
          {readOnly ? (
            <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md text-gray-700">
              {displayJobNumber || 'N/A'}{displayJobDescription}
            </div>
          ) : (
            <select
              value={data?.job || ''}
              onChange={handleJobChange}
              className="w-full px-3 py-2 border h-[37px] border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="">Select Job ID</option>
              {activeJobsList?.map((job) => (
                <option key={job.id} value={job.id}>
                  {job.number} - {job.description}
                </option>
              ))}
            </select>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Date Performed *
          </label>
          {readOnly ? (
            <div className="p-2 bg-gray-50 border border-gray-300 rounded-md text-gray-700">
              {data?.date_performed || 'N/A'}
            </div>
          ) : (
            <input
              type="date"
              value={data?.date_performed || getTodayInPT()}
              onChange={handleDateChange}
              className="w-full px-3 py-2 max-h-[37px] border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default JobInformationSection;
